import { ArrowLeft, Bell, Menu } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotificationsSummary } from '../contexts/NotificationsContext';
import { getCurrentNavItem, getCurrentPageTitle } from './appNavigation';
import AppAvatar from './ui/AppAvatar';

function PageHeader({ currentUser, onOpenMenu }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useNotificationsSummary();
  const currentNavItem = getCurrentNavItem(location.pathname);
  const canGoBack = location.pathname !== '/history';

  return (
    <header className="sticky top-0 z-50 border-b border-app-border/80 bg-app-surface/90 shadow-subtle backdrop-blur-md">
      <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-4 md:px-6">
        <div className="flex items-center gap-3">
          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-app-border bg-app-surface text-app-ink shadow-subtle transition hover:bg-app-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-accent/35 md:hidden"
            onClick={onOpenMenu}
            title="Open menu"
            aria-label="Open menu"
            type="button"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>

          {canGoBack && (
            <button
              className="hidden items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-semibold text-app-ink shadow-subtle transition hover:-translate-x-0.5 hover:bg-app-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-accent/35 md:inline-flex"
              onClick={() => navigate(-1)}
              title="Go back"
              aria-label="Go back"
              type="button"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span>Back</span>
            </button>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-app-muted">
            {currentNavItem?.description || 'Workspace'}
          </p>
          <h1 className="m-0 truncate text-2xl font-bold tracking-tight text-app-ink">
            {getCurrentPageTitle(location.pathname)}
          </h1>
        </div>

        <button
          className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl border border-app-border bg-app-surface text-app-ink shadow-subtle transition hover:bg-app-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-accent/35"
          onClick={() => navigate('/notifications')}
          title="Open notifications"
          aria-label={unreadCount ? `Open notifications (${unreadCount} unread)` : 'Open notifications'}
          type="button"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unreadCount ? (
            <span className="absolute -right-1.5 -top-1.5 inline-flex min-w-[1.35rem] items-center justify-center rounded-full bg-app-accent px-1.5 py-0.5 text-[11px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </button>

        <div className="hidden items-center gap-3 rounded-2xl border border-app-border/80 bg-app-surface px-3 py-2 shadow-subtle sm:flex">
          <div className="text-right">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
              Active workspace
            </p>
            <p className="m-0 max-w-[220px] truncate text-sm font-medium text-app-ink">
              {currentUser?.email || 'User'}
            </p>
          </div>
          <AppAvatar email={currentUser?.email} size="md" />
        </div>

        <div className="sm:hidden">
          <AppAvatar email={currentUser?.email} size="sm" />
        </div>
      </div>
    </header>
  );
}

export default PageHeader;
